import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
    financeText,
    getFinanceLanguage,
    useFinanceDocumentLang,
    saveFinanceLanguage,
} from "finance-language";

import { Alert, Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, MenuItem, Snackbar, Stack, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import {
    AddOutlined,
    ArrowDownward,
    ArrowUpward,
    CheckCircleOutline,
    DeleteOutlineOutlined,
    EditOutlined,
    EventOutlined,
    ReceiptLongOutlined,
    RefreshOutlined,
    SearchOutlined,
    TaskAltOutlined,
    WarningAmberRounded,
} from "@mui/icons-material";

import {
    FinanceKpiTile,
} from "../crm/invoice/finance/shared/FinanceUiKit";
// Koyu hero bandı için (yedekten geri yüklendi)
import { AccountBalanceOutlined } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";
/* =========================================================
   CONFIG
========================================================= */

const API_URL =
    process.env.REACT_APP_API_URL;

/*
 * Backend:
 *
 * .use("/finance/cash", cashRoutes)
 *
 * Actual endpoint:
 * /api/finance/cash/accounts
 */
const CASH_API =
    "/finance/cash";

/* =========================================================
   CONSTANTS
========================================================= */

const CURRENCIES = [
    "TRY",
    "USD",
    "EUR",
    "GBP",
];

const DIRECTIONS = [
    {
        value: "received",
        label: "Received Cheque",
    },
    {
        value: "issued",
        label: "Issued Cheque",
    },
];

const STATUSES = [
    {
        value: "portfolio",
        label: "Portfolio",
    },
    {
        value: "deposited",
        label: "Deposited",
    },
    {
        value: "endorsed",
        label: "Endorsed",
    },
    {
        value: "collected",
        label: "Collected",
    },
    {
        value: "paid",
        label: "Paid",
    },
    {
        value: "bounced",
        label: "Bounced",
    },
    {
        value: "cancelled",
        label: "Cancelled",
    },
];

/*
 * Kullanıcının manuel seçebileceği status'lar.
 *
 * Collected ve Paid sadece /settle endpoint'i
 * üzerinden oluşur.
 */
const RECEIVED_EDITABLE_STATUSES = [
    "portfolio",
    "deposited",
    "endorsed",
    "bounced",
    "cancelled",
];

const ISSUED_EDITABLE_STATUSES = [
    "portfolio",
    "cancelled",
];

const emptyChequeForm = {
    chequeNo: "",
    direction: "received",
    amount: "",
    currency: "TRY",

    bankName: "",
    branch: "",
    accountNo: "",

    drawerName: "",
    beneficiaryName: "",

    issueDate: "",
    dueDate: "",

    customerId: "",
    serviceProviderId: "",

    status: "portfolio",
    description: "",

    isActive: true,
};

const emptySettleForm = {
    cashAccountId: "",
};

/* =========================================================
   COLORS
========================================================= */

const COLORS = {
    navy: "#0B1F3A",

    blue: "#175CD3",
    blueLight: "#EFF8FF",

    green: "#027A48",
    greenLight: "#ECFDF3",

    red: "#B42318",
    redLight: "#FEF3F2",

    amber: "#B54708",
    amberLight: "#FFFAEB",

    purple: "#6941C6",
    purpleLight: "#F4F3FF",

    text: "#101828",
    subtext: "#667085",

    border: "#E4E7EC",

    page: "#F5F7FA",
    white: "#FFFFFF",
};

const cardSx = {
    borderRadius: "10px",

    background:
    COLORS.white,

    border: "1px solid rgba(15, 23, 42, 0.12)",

    boxShadow: "none",

    overflow:
        "hidden",
};

/* =========================================================
   HELPERS
========================================================= */

const getId = (
    value
) => {
    if (!value) {
        return "";
    }

    if (
        typeof value ===
        "object"
    ) {
        return String(
            value._id ||
            value.id ||
            ""
        );
    }

    return String(
        value
    );
};

const normalizeText = (
    value
) =>
    String(
        value || ""
    ).trim();

/* =========================================================
   NORMALIZE LIST
========================================================= */

const normalizeList = (
    payload
) => {
    let items =
        [];

    if (
        Array.isArray(
            payload
        )
    ) {
        items =
            payload;
    } else if (
        Array.isArray(
            payload?.data
        )
    ) {
        items =
            payload.data;
    } else if (
        Array.isArray(
            payload?.data?.data
        )
    ) {
        items =
            payload.data.data;
    } else if (
        Array.isArray(
            payload?.results
        )
    ) {
        items =
            payload.results;
    } else if (
        Array.isArray(
            payload?.cheques
        )
    ) {
        items =
            payload.cheques;
    } else if (
        Array.isArray(
            payload?.accounts
        )
    ) {
        items =
            payload.accounts;
    } else if (
        Array.isArray(
            payload?.data?.cheques
        )
    ) {
        items =
            payload.data.cheques;
    } else if (
        Array.isArray(
            payload?.data?.accounts
        )
    ) {
        items =
            payload.data.accounts;
    }

    return items.map(
        (
            item
        ) => {
            if (
                item?.attributes
            ) {
                return {
                    _id:
                        item.id ||
                        item.attributes?._id,

                    ...item.attributes,
                };
            }

            return item;
        }
    );
};

/* =========================================================
   MONEY
========================================================= */

const formatMoney = (
    amount,
    currency = "TRY"
) => {
    try {
        return new Intl.NumberFormat(
            "tr-TR",
            {
                style:
                    "currency",

                currency,

                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,
            }
        ).format(
            Number(
                amount
            ) ||
            0
        );
    } catch {
        return `${
            Number(
                amount
            ) ||
            0
        } ${currency}`;
    }
};

/* =========================================================
   DATE
========================================================= */

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleDateString(
        "tr-TR"
    );
};

const formatDateTime = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleString(
        "tr-TR"
    );
};

const toInputDate = (
    value
) => {
    if (!value) {
        return "";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    const year =
        date.getFullYear();

    const month =
        String(
            date.getMonth() +
            1
        ).padStart(
            2,
            "0"
        );

    const day =
        String(
            date.getDate()
        ).padStart(
            2,
            "0"
        );

    return `${year}-${month}-${day}`;
};

/* =========================================================
   DUE DATE
========================================================= */

const getDaysUntilDue = (
    value
) => {
    if (!value) {
        return null;
    }

    const due =
        new Date(
            value
        );

    if (
        Number.isNaN(
            due.getTime()
        )
    ) {
        return null;
    }

    due.setHours(
        0,
        0,
        0,
        0
    );

    const today =
        new Date();

    today.setHours(
        0,
        0,
        0,
        0
    );

    return Math.ceil(
        (
            due.getTime() -
            today.getTime()
        ) /
        (
            1000 *
            60 *
            60 *
            24
        )
    );
};

/* =========================================================
   DIRECTION
========================================================= */

const getDirectionConfig = (
    direction
) => {
    if (
        direction ===
        "issued"
    ) {
        return {
            label:
                "Issued",

            color:
            COLORS.red,

            background:
            COLORS.redLight,

            border:
                "#FECDCA",

            icon:
                <ArrowUpward fontSize="small" />,
        };
    }

    return {
        label:
            "Received",

        color:
        COLORS.green,

        background:
        COLORS.greenLight,

        border:
            "#ABEFC6",

        icon:
            <ArrowDownward fontSize="small" />,
    };
};

/* =========================================================
   STATUS
========================================================= */

const getStatusConfig = (
    status
) => {
    switch (
        status
        ) {
        case "collected":
            return {
                label:
                    "Collected",

                color:
                COLORS.green,

                background:
                COLORS.greenLight,

                border:
                    "#ABEFC6",
            };

        case "paid":
            return {
                label:
                    "Paid",

                color:
                COLORS.green,

                background:
                COLORS.greenLight,

                border:
                    "#ABEFC6",
            };

        case "deposited":
            return {
                label:
                    "Deposited",

                color:
                COLORS.blue,

                background:
                COLORS.blueLight,

                border:
                    "#B2DDFF",
            };

        case "endorsed":
            return {
                label:
                    "Endorsed",

                color:
                COLORS.purple,

                background:
                COLORS.purpleLight,

                border:
                    "#D9D6FE",
            };

        case "bounced":
            return {
                label:
                    "Bounced",

                color:
                COLORS.red,

                background:
                COLORS.redLight,

                border:
                    "#FECDCA",
            };

        case "cancelled":
            return {
                label:
                    "Cancelled",

                color:
                COLORS.red,

                background:
                COLORS.redLight,

                border:
                    "#FECDCA",
            };

        default:
            return {
                label:
                    "Portfolio",

                color:
                COLORS.amber,

                background:
                COLORS.amberLight,

                border:
                    "#FEDF89",
            };
    }
};

/* =========================================================
   BUSINESS RULES
========================================================= */

const isSettledCheque = (
    cheque
) =>
    [
        "collected",
        "paid",
    ].includes(
        cheque?.status
    );

const canSettleCheque = (
    cheque
) => {
    if (!cheque) {
        return false;
    }

    if (
        cheque.isActive ===
        false
    ) {
        return false;
    }

    /*
     * Received:
     *
     * Portfolio -> Collect
     * Deposited -> Collect
     *
     * Endorsed çek artık başka kişiye devredilmiştir;
     * Company Cash'e doğrudan tahsil edilmemeli.
     *
     * Bounced çek doğrudan tahsil edilmemeli.
     */
    if (
        cheque.direction ===
        "received"
    ) {
        return [
            "portfolio",
            "deposited",
        ].includes(
            cheque.status
        );
    }

    /*
     * Issued:
     *
     * Portfolio -> Pay
     */
    if (
        cheque.direction ===
        "issued"
    ) {
        return (
            cheque.status ===
            "portfolio"
        );
    }

    return false;
};

const getEditableStatusesForDirection = (
    direction
) => {
    if (
        direction ===
        "issued"
    ) {
        return STATUSES.filter(
            (
                item
            ) =>
                ISSUED_EDITABLE_STATUSES.includes(
                    item.value
                )
        );
    }

    return STATUSES.filter(
        (
            item
        ) =>
            RECEIVED_EDITABLE_STATUSES.includes(
                item.value
            )
    );
};

/* =========================================================
   ACTION BUTTON
========================================================= */

const getActionButtonSx = (
    tone
) => {
    const configs = {
        settle: {
            color:
            COLORS.green,

            background:
            COLORS.greenLight,

            border:
                "#ABEFC6",

            hover:
                "#D1FADF",
        },

        edit: {
            color:
            COLORS.purple,

            background:
            COLORS.purpleLight,

            border:
                "#D9D6FE",

            hover:
                "#E9E7FD",
        },

        delete: {
            color:
            COLORS.red,

            background:
            COLORS.redLight,

            border:
                "#FECDCA",

            hover:
                "#FEE4E2",
        },
    };

    const config =
        configs[tone] ||
        configs.edit;

    return {
        width:
            36,

        height:
            36,

        color:
        config.color,

        background:
        config.background,

        border:
            `1px solid ${config.border}`,

        borderRadius:
            "9px",

        "&:hover": {
            background:
            config.hover,
        },

        "&.Mui-disabled": {
            color:
                "#98A2B3",

            background:
                "#F2F4F7",

            border:
                `1px solid ${COLORS.border}`,
        },
    };
};

/* =========================================================
   METRIC CARD
========================================================= */

// Görünüm Finance kitindeki KPI kartıyla aynı (bkz. shared/FinanceUiKit.js)
const MetricCard = (props) => <FinanceKpiTile {...props} />;

/* =========================================================
   MAIN
========================================================= */

const Cheques = () => {
    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    useFinanceDocumentLang(financeLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const token =
        localStorage.getItem(
            "token"
        );

    /* =====================================================
       DATA
    ===================================================== */

    const [
        cheques,
        setCheques,
    ] = useState([]);

    const [
        cashAccounts,
        setCashAccounts,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        saving,
        setSaving,
    ] = useState(false);

    const [
        lastUpdated,
        setLastUpdated,
    ] = useState(
        new Date()
    );

    /* =====================================================
       FILTERS
    ===================================================== */

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        directionFilter,
        setDirectionFilter,
    ] = useState(
        "all"
    );

    const [
        statusFilter,
        setStatusFilter,
    ] = useState(
        "all"
    );

    const [
        currencyFilter,
        setCurrencyFilter,
    ] = useState(
        "all"
    );

    const [
        summaryCurrency,
        setSummaryCurrency,
    ] = useState(
        "TRY"
    );

    /* =====================================================
       CREATE / EDIT
    ===================================================== */

    const [
        chequeModal,
        setChequeModal,
    ] = useState(false);

    const [
        editingCheque,
        setEditingCheque,
    ] = useState(null);

    const [
        chequeForm,
        setChequeForm,
    ] = useState(
        emptyChequeForm
    );

    /* =====================================================
       SETTLE
    ===================================================== */

    const [
        settleTarget,
        setSettleTarget,
    ] = useState(null);

    const [
        settleForm,
        setSettleForm,
    ] = useState(
        emptySettleForm
    );

    /* =====================================================
       DELETE
    ===================================================== */

    const [
        deleteTarget,
        setDeleteTarget,
    ] = useState(null);

    /* =====================================================
       NOTIFICATION
    ===================================================== */

    const [
        notification,
        setNotification,
    ] = useState({
        open:
            false,

        message:
            "",

        severity:
            "success",
    });

    const showNotification =
        useCallback(
            (
                message,
                severity =
                    "success"
            ) => {
                setNotification({
                    open:
                        true,

                    message,

                    severity,
                });
            },

            []
        );

    const closeNotification =
        () => {
            setNotification(
                (
                    previous
                ) => ({
                    ...previous,

                    open:
                        false,
                })
            );
        };

    /* =====================================================
       API
    ===================================================== */

    const apiRequest =
        useCallback(
            async (
                path,
                options = {}
            ) => {
                const baseUrl =
                    String(
                        API_URL ||
                        ""
                    ).replace(
                        /\/+$/,
                        ""
                    );

                const headers = {
                    "Content-Type":
                        "application/json",

                    ...(options.headers ||
                        {}),
                };

                if (token) {
                    headers.Authorization =
                        `Bearer ${token}`;
                }

                const response =
                    await fetch(
                        `${baseUrl}${path}`,

                        {
                            ...options,

                            headers,
                        }
                    );

                let body =
                    null;

                try {
                    body =
                        await response.json();
                } catch {
                    body =
                        null;
                }

                if (
                    !response.ok
                ) {
                    throw new Error(
                        body
                            ?.errors?.[0]
                            ?.detail ||
                        body
                            ?.message ||
                        `Request failed with status ${response.status}.`
                    );
                }

                return body;
            },

            [
                token,
            ]
        );

    /* =====================================================
       FETCH
    ===================================================== */

    const fetchData =
        useCallback(
            async () => {
                setLoading(
                    true
                );

                try {
                    const results =
                        await Promise.allSettled(
                            [
                                apiRequest(
                                    "/cheques"
                                ),

                                apiRequest(
                                    `${CASH_API}/accounts`
                                ),
                            ]
                        );

                    const [
                        chequeResult,
                        cashResult,
                    ] =
                        results;

                    /* =====================================
                       CHEQUES
                    ===================================== */

                    if (
                        chequeResult.status ===
                        "fulfilled"
                    ) {
                        setCheques(
                            normalizeList(
                                chequeResult.value
                            )
                        );
                    } else {
                        console.error(
                            "Cheques request failed:",
                            chequeResult.reason
                        );

                        setCheques(
                            []
                        );

                        showNotification(
                            financeText("Cheques could not be loaded."),

                            "error"
                        );
                    }

                    /* =====================================
                       CASH
                    ===================================== */

                    if (
                        cashResult.status ===
                        "fulfilled"
                    ) {
                        setCashAccounts(
                            normalizeList(
                                cashResult.value
                            )
                        );
                    } else {
                        console.error(
                            "Cash accounts request failed:",
                            cashResult.reason
                        );

                        setCashAccounts(
                            []
                        );

                        showNotification(
                            financeText("Company Cash accounts could not be loaded."),

                            "error"
                        );
                    }

                    setLastUpdated(
                        new Date()
                    );
                } catch (
                    error
                    ) {
                    console.error(
                        "Cheque fetch error:",
                        error
                    );

                    showNotification(
                        error?.message ||
                        financeText("Cheque data could not be loaded."),

                        "error"
                    );
                } finally {
                    setLoading(
                        false
                    );
                }
            },

            [
                apiRequest,
                showNotification,
            ]
        );

    useEffect(
        () => {
            fetchData();
        },

        [
            fetchData,
        ]
    );

    /* =====================================================
       CASH ACCOUNT RESOLVER
    ===================================================== */

    const resolveCashAccount =
        useCallback(
            (
                value
            ) => {
                if (!value) {
                    return null;
                }

                if (
                    typeof value ===
                    "object"
                ) {
                    return value;
                }

                return (
                    cashAccounts.find(
                        (
                            account
                        ) =>
                            getId(
                                account
                            ) ===
                            String(
                                value
                            )
                    ) ||
                    null
                );
            },

            [
                cashAccounts,
            ]
        );

    /* =====================================================
       CHEQUE SETTLEMENT ACCOUNT

       Yeni backend:
       settlementAccountId

       Eski frontend fallback:
       linkedCashAccountId
    ===================================================== */

    const resolveChequeSettlementAccount =
        useCallback(
            (
                cheque
            ) => {
                if (!cheque) {
                    return null;
                }

                return resolveCashAccount(
                    cheque.settlementAccountId ||
                    cheque.linkedCashAccountId
                );
            },

            [
                resolveCashAccount,
            ]
        );

    /* =====================================================
       SUMMARY
    ===================================================== */

    const summary =
        useMemo(
            () => {
                const result = {
                    receivedPortfolio:
                        0,

                    issuedPortfolio:
                        0,

                    overdue:
                        0,

                    dueSoon:
                        0,

                    receivedCount:
                        0,

                    issuedCount:
                        0,
                };

                cheques.forEach(
                    (
                        cheque
                    ) => {
                        if (
                            cheque.currency !==
                            summaryCurrency
                        ) {
                            return;
                        }

                        const amount =
                            Number(
                                cheque.amount
                            ) ||
                            0;

                        if (
                            cheque.direction ===
                            "received"
                        ) {
                            result.receivedCount +=
                                1;

                            /*
                             * Gerçek portfolio:
                             * portfolio/deposited
                             *
                             * endorsed artık şirkette değil.
                             * collected tamamlandı.
                             * bounced cashable değil.
                             */
                            if (
                                [
                                    "portfolio",
                                    "deposited",
                                ].includes(
                                    cheque.status
                                )
                            ) {
                                result.receivedPortfolio +=
                                    amount;
                            }
                        }

                        if (
                            cheque.direction ===
                            "issued"
                        ) {
                            result.issuedCount +=
                                1;

                            if (
                                cheque.status ===
                                "portfolio"
                            ) {
                                result.issuedPortfolio +=
                                    amount;
                            }
                        }

                        /*
                         * Due / overdue sadece aktif olarak
                         * settlement bekleyen çekler için.
                         */

                        if (
                            canSettleCheque(
                                cheque
                            )
                        ) {
                            const days =
                                getDaysUntilDue(
                                    cheque.dueDate
                                );

                            if (
                                days !==
                                null &&
                                days <
                                0
                            ) {
                                result.overdue +=
                                    1;
                            }

                            if (
                                days !==
                                null &&
                                days >=
                                0 &&
                                days <=
                                7
                            ) {
                                result.dueSoon +=
                                    1;
                            }
                        }
                    }
                );

                return result;
            },

            [
                cheques,
                summaryCurrency,
            ]
        );

    /* =====================================================
       FILTER
    ===================================================== */

    const filteredCheques =
        useMemo(
            () => {
                const query =
                    search
                        .trim()
                        .toLowerCase();

                return cheques.filter(
                    (
                        cheque
                    ) => {
                        const account =
                            resolveChequeSettlementAccount(
                                cheque
                            );

                        const searchMatch =
                            !query ||
                            [
                                cheque.chequeNo,
                                cheque.bankName,
                                cheque.branch,
                                cheque.accountNo,
                                cheque.drawerName,
                                cheque.beneficiaryName,
                                cheque.description,
                                cheque.currency,
                                account?.name,
                            ].some(
                                (
                                    value
                                ) =>
                                    String(
                                        value ||
                                        ""
                                    )
                                        .toLowerCase()
                                        .includes(
                                            query
                                        )
                            );

                        const directionMatch =
                            directionFilter ===
                            "all" ||
                            cheque.direction ===
                            directionFilter;

                        const statusMatch =
                            statusFilter ===
                            "all" ||
                            cheque.status ===
                            statusFilter;

                        const currencyMatch =
                            currencyFilter ===
                            "all" ||
                            cheque.currency ===
                            currencyFilter;

                        return (
                            searchMatch &&
                            directionMatch &&
                            statusMatch &&
                            currencyMatch
                        );
                    }
                );
            },

            [
                cheques,
                search,
                directionFilter,
                statusFilter,
                currencyFilter,
                resolveChequeSettlementAccount,
            ]
        );

    /* =====================================================
       SETTLEMENT ACCOUNTS
    ===================================================== */

    const settlementAccounts =
        useMemo(
            () => {
                if (
                    !settleTarget
                ) {
                    return [];
                }

                return cashAccounts.filter(
                    (
                        account
                    ) =>
                        account.isActive !==
                        false &&
                        account.currency ===
                        settleTarget.currency
                );
            },

            [
                cashAccounts,
                settleTarget,
            ]
        );

    const selectedSettlementAccount =
        useMemo(
            () =>
                settlementAccounts.find(
                    (
                        account
                    ) =>
                        getId(
                            account
                        ) ===
                        String(
                            settleForm.cashAccountId
                        )
                ) ||
                null,

            [
                settlementAccounts,
                settleForm.cashAccountId,
            ]
        );

    /* =====================================================
       SETTLEMENT PREVIEW
    ===================================================== */

    const settlementPreview =
        useMemo(
            () => {
                if (
                    !settleTarget ||
                    !selectedSettlementAccount
                ) {
                    return null;
                }

                const amount =
                    Number(
                        settleTarget.amount ||
                        0
                    );

                const currentBalance =
                    Number(
                        selectedSettlementAccount.currentBalance ||
                        0
                    );

                const balanceAfter =
                    settleTarget.direction ===
                    "received"
                        ? currentBalance +
                        amount
                        : currentBalance -
                        amount;

                return {
                    amount,

                    currentBalance,

                    balanceAfter,

                    insufficientBalance:
                        settleTarget.direction ===
                        "issued" &&
                        amount >
                        currentBalance,
                };
            },

            [
                settleTarget,
                selectedSettlementAccount,
            ]
        );

    /* =====================================================
       ADD
    ===================================================== */

    const openAddCheque =
        () => {
            setEditingCheque(
                null
            );

            setChequeForm(
                emptyChequeForm
            );

            setChequeModal(
                true
            );
        };

    /* =====================================================
       EDIT
    ===================================================== */

    const openEditCheque =
        (
            cheque
        ) => {
            if (
                isSettledCheque(
                    cheque
                )
            ) {
                showNotification(
                    financeText("Settled cheques cannot be edited."),

                    "warning"
                );

                return;
            }

            setEditingCheque(
                cheque
            );

            setChequeForm({
                chequeNo:
                    cheque.chequeNo ||
                    "",

                direction:
                    cheque.direction ||
                    "received",

                amount:
                    cheque.amount ??
                    "",

                currency:
                    cheque.currency ||
                    "TRY",

                bankName:
                    cheque.bankName ||
                    "",

                branch:
                    cheque.branch ||
                    "",

                accountNo:
                    cheque.accountNo ||
                    "",

                drawerName:
                    cheque.drawerName ||
                    "",

                beneficiaryName:
                    cheque.beneficiaryName ||
                    "",

                issueDate:
                    toInputDate(
                        cheque.issueDate
                    ),

                dueDate:
                    toInputDate(
                        cheque.dueDate
                    ),

                customerId:
                    getId(
                        cheque.customerId
                    ),

                serviceProviderId:
                    getId(
                        cheque.serviceProviderId
                    ),

                status:
                    cheque.status ||
                    "portfolio",

                description:
                    cheque.description ||
                    "",

                isActive:
                    cheque.isActive !==
                    false,
            });

            setChequeModal(
                true
            );
        };

    const closeChequeModal =
        () => {
            if (
                saving
            ) {
                return;
            }

            setChequeModal(
                false
            );

            setEditingCheque(
                null
            );

            setChequeForm(
                emptyChequeForm
            );
        };

    /* =====================================================
       DIRECTION CHANGE
    ===================================================== */

    const handleDirectionChange =
        (
            direction
        ) => {
            setChequeForm(
                (
                    previous
                ) => ({
                    ...previous,

                    direction,

                    /*
                     * Direction değişirse eski status
                     * taşınmasın.
                     */
                    status:
                        "portfolio",
                })
            );
        };

    /* =====================================================
       SAVE
    ===================================================== */

    const saveCheque =
        async () => {
            try {
                /* =========================================
                   CHEQUE NUMBER
                ========================================= */

                if (
                    !normalizeText(
                        chequeForm.chequeNo
                    )
                ) {
                    showNotification(
                        financeText("Cheque number is required."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   AMOUNT
                ========================================= */

                const amount =
                    Number(
                        chequeForm.amount
                    );

                if (
                    !Number.isFinite(
                        amount
                    ) ||
                    amount <=
                    0
                ) {
                    showNotification(
                        financeText("Cheque amount must be greater than zero."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   DUE DATE
                ========================================= */

                if (
                    !chequeForm.dueDate
                ) {
                    showNotification(
                        financeText("Due date is required."),

                        "warning"
                    );

                    return;
                }

                if (
                    chequeForm.issueDate &&
                    chequeForm.dueDate <
                    chequeForm.issueDate
                ) {
                    showNotification(
                        financeText("Due date cannot be earlier than the issue date."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   STATUS BUSINESS RULE
                ========================================= */

                const allowedStatuses =
                    chequeForm.direction ===
                    "received"
                        ? RECEIVED_EDITABLE_STATUSES
                        : ISSUED_EDITABLE_STATUSES;

                if (
                    !allowedStatuses.includes(
                        chequeForm.status
                    )
                ) {
                    showNotification(
                        financeText("Selected status is not valid for this cheque direction."),

                        "warning"
                    );

                    return;
                }

                const payload = {
                    chequeNo:
                        normalizeText(
                            chequeForm.chequeNo
                        ),

                    direction:
                    chequeForm.direction,

                    amount,

                    currency:
                    chequeForm.currency,

                    bankName:
                        normalizeText(
                            chequeForm.bankName
                        ),

                    branch:
                        normalizeText(
                            chequeForm.branch
                        ),

                    accountNo:
                        normalizeText(
                            chequeForm.accountNo
                        ),

                    drawerName:
                        normalizeText(
                            chequeForm.drawerName
                        ),

                    beneficiaryName:
                        normalizeText(
                            chequeForm.beneficiaryName
                        ),

                    issueDate:
                        chequeForm.issueDate ||
                        null,

                    dueDate:
                    chequeForm.dueDate,

                    customerId:
                        chequeForm.customerId ||
                        null,

                    serviceProviderId:
                        chequeForm.serviceProviderId ||
                        null,

                    status:
                    chequeForm.status,

                    description:
                        normalizeText(
                            chequeForm.description
                        ),

                    isActive:
                    chequeForm.isActive,
                };

                setSaving(
                    true
                );

                if (
                    editingCheque
                ) {
                    await apiRequest(
                        `/cheques/${getId(
                            editingCheque
                        )}`,

                        {
                            method:
                                "PATCH",

                            body:
                                JSON.stringify(
                                    payload
                                ),
                        }
                    );

                    showNotification(
                        financeText("Cheque updated successfully.")
                    );
                } else {
                    await apiRequest(
                        "/cheques",

                        {
                            method:
                                "POST",

                            body:
                                JSON.stringify(
                                    payload
                                ),
                        }
                    );

                    showNotification(
                        financeText("Cheque created successfully.")
                    );
                }

                setChequeModal(
                    false
                );

                setEditingCheque(
                    null
                );

                setChequeForm(
                    emptyChequeForm
                );

                await fetchData();
            } catch (
                error
                ) {
                showNotification(
                    error?.message ||
                    financeText("Cheque could not be saved."),

                    "error"
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    /* =====================================================
       OPEN SETTLEMENT
    ===================================================== */

    const openSettle =
        (
            cheque
        ) => {
            if (
                !canSettleCheque(
                    cheque
                )
            ) {
                showNotification(
                    cheque.direction ===
                    "received"
                        ? financeText("Only received cheques in Portfolio or Deposited status can be collected.")
                        : financeText("Only issued cheques in Portfolio status can be paid."),

                    "warning"
                );

                return;
            }

            setSettleTarget(
                cheque
            );

            setSettleForm(
                emptySettleForm
            );
        };

    const closeSettle =
        () => {
            if (
                saving
            ) {
                return;
            }

            setSettleTarget(
                null
            );

            setSettleForm(
                emptySettleForm
            );
        };

    /* =====================================================
       SETTLE

       Received:
       Company Cash += cheque amount
       status = collected
       transaction = cash_in / cheque

       Issued:
       Company Cash -= cheque amount
       status = paid
       transaction = cash_out / cheque
    ===================================================== */

    const settleCheque =
        async () => {
            if (
                !settleTarget
            ) {
                return;
            }

            try {
                /* =========================================
                   CURRENT STATUS
                ========================================= */

                if (
                    !canSettleCheque(
                        settleTarget
                    )
                ) {
                    showNotification(
                        financeText("Cheque can no longer be settled in its current status."),

                        "warning"
                    );

                    setSettleTarget(
                        null
                    );

                    await fetchData();

                    return;
                }

                /* =========================================
                   ACCOUNT
                ========================================= */

                if (
                    !settleForm.cashAccountId
                ) {
                    showNotification(
                        financeText("Please select a Company Cash account."),

                        "warning"
                    );

                    return;
                }

                if (
                    !selectedSettlementAccount
                ) {
                    showNotification(
                        financeText("Selected Company Cash account could not be found."),

                        "warning"
                    );

                    return;
                }

                if (
                    selectedSettlementAccount.isActive ===
                    false
                ) {
                    showNotification(
                        financeText("Selected Company Cash account is inactive."),

                        "warning"
                    );

                    return;
                }

                if (
                    selectedSettlementAccount.currency !==
                    settleTarget.currency
                ) {
                    showNotification(
                        financeText("Cheque and Company Cash account currencies must match."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   BALANCE
                ========================================= */

                const amount =
                    Number(
                        settleTarget.amount ||
                        0
                    );

                if (
                    settleTarget.direction ===
                    "issued" &&
                    amount >
                    Number(
                        selectedSettlementAccount.currentBalance ||
                        0
                    )
                ) {
                    showNotification(
                        financeText("Selected account has insufficient balance."),

                        "warning"
                    );

                    return;
                }

                setSaving(
                    true
                );

                await apiRequest(
                    `/cheques/${getId(
                        settleTarget
                    )}/settle`,

                    {
                        method:
                            "POST",

                        body:
                            JSON.stringify({
                                /*
                                 * Backend settlement service
                                 * şu an bu field'i bekliyor.
                                 */
                                cashAccountId:
                                settleForm.cashAccountId,
                            }),
                    }
                );

                const direction =
                    settleTarget.direction;

                setSettleTarget(
                    null
                );

                setSettleForm(
                    emptySettleForm
                );

                await fetchData();

                showNotification(
                    direction ===
                    "received"
                        ? financeText("Cheque collected successfully and added to Company Cash.")
                        : financeText("Cheque paid successfully and deducted from Company Cash.")
                );
            } catch (
                error
                ) {
                showNotification(
                    error?.message ||
                    financeText("Cheque could not be settled."),

                    "error"
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    /* =====================================================
       DELETE
    ===================================================== */

    const openDelete =
        (
            cheque
        ) => {
            if (
                isSettledCheque(
                    cheque
                )
            ) {
                showNotification(
                    financeText("Collected or paid cheques cannot be deleted because they already affected Company Cash."),

                    "warning"
                );

                return;
            }

            setDeleteTarget(
                cheque
            );
        };

    const confirmDelete =
        async () => {
            if (
                !deleteTarget
            ) {
                return;
            }

            if (
                isSettledCheque(
                    deleteTarget
                )
            ) {
                showNotification(
                    financeText("Settled cheque cannot be deleted."),

                    "warning"
                );

                setDeleteTarget(
                    null
                );

                return;
            }

            try {
                setSaving(
                    true
                );

                await apiRequest(
                    `/cheques/${getId(
                        deleteTarget
                    )}`,

                    {
                        method:
                            "DELETE",
                    }
                );

                setDeleteTarget(
                    null
                );

                await fetchData();

                showNotification(
                    financeText("Cheque deleted successfully.")
                );
            } catch (
                error
                ) {
                showNotification(
                    error?.message ||
                    financeText("Cheque could not be deleted."),

                    "error"
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    /* =====================================================
       TABLE
    ===================================================== */

    const tableData =
        useMemo(
            () => ({
                columns: [
                    /* =====================================
                       CHEQUE
                    ===================================== */

                    {
                        Header:
                            financeText("CHEQUE"),

                        accessor:
                            "chequeNo",

                        Cell: ({
                                   row,
                               }) => {
                            const cheque =
                                row.original;

                            const direction =
                                getDirectionConfig(
                                    cheque.direction
                                );

                            return (
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <MDBox
                                        width={42}
                                        height={42}
                                        minWidth={42}
                                        borderRadius="8px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            color:
                                            direction.color,

                                            background:
                                            direction.background,

                                            border:
                                                `1px solid ${direction.border}`,
                                        }}
                                    >
                                        <ReceiptLongOutlined />
                                    </MDBox>

                                    <MDBox>
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.text,
                                            }}
                                        >
                                            {cheque.chequeNo ||
                                                "-"}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Created")}{" "}
                                            {formatDate(
                                                cheque.createdAt
                                            )}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       DIRECTION
                    ===================================== */

                    {
                        Header:
                            financeText("DIRECTION"),

                        accessor:
                            "direction",

                        Cell: ({
                                   value,
                               }) => {
                            const config =
                                getDirectionConfig(
                                    value
                                );

                            return (
                                <Chip
                                    size="small"
                                    icon={
                                        config.icon
                                    }
                                    label={
                                        financeText(
                                            config.label
                                        )
                                    }
                                    sx={{
                                        color:
                                        config.color,

                                        background:
                                        config.background,

                                        border:
                                            `1px solid ${config.border}`,

                                        fontWeight:
                                            700,

                                        "& .MuiChip-icon":
                                            {
                                                color:
                                                    `${config.color} !important`,
                                            },
                                    }}
                                />
                            );
                        },
                    },

                    /* =====================================
                       PARTY
                    ===================================== */

                    {
                        Header:
                            financeText("PARTY"),

                        id:
                            "party",

                        Cell: ({
                                   row,
                               }) => {
                            const cheque =
                                row.original;

                            return (
                                <MDBox>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.text,
                                        }}
                                    >
                                        {cheque.direction ===
                                        "received"
                                            ? cheque.drawerName ||
                                            "-"
                                            : cheque.beneficiaryName ||
                                            "-"}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {cheque.direction ===
                                        "received"
                                            ? financeText("Drawer")
                                            : financeText("Beneficiary")}
                                    </MDTypography>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       BANK
                    ===================================== */

                    {
                        Header:
                            financeText("BANK"),

                        accessor:
                            "bankName",

                        Cell: ({
                                   row,
                               }) => {
                            const cheque =
                                row.original;

                            return (
                                <MDBox>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.text,
                                        }}
                                    >
                                        {cheque.bankName ||
                                            "-"}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {cheque.branch ||
                                            "-"}
                                    </MDTypography>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       CURRENCY
                    ===================================== */

                    {
                        Header:
                            financeText("CURRENCY"),

                        accessor:
                            "currency",

                        Cell: ({
                                   value,
                               }) => (
                            <Chip
                                size="small"
                                label={
                                    value ||
                                    "-"
                                }
                                sx={{
                                    color:
                                    COLORS.blue,

                                    background:
                                    COLORS.blueLight,

                                    border:
                                        "1px solid #B2DDFF",

                                    fontWeight:
                                        800,
                                }}
                            />
                        ),
                    },

                    /* =====================================
                       AMOUNT
                    ===================================== */

                    {
                        Header:
                            financeText("AMOUNT"),

                        accessor:
                            "amount",

                        Cell: ({
                                   row,
                               }) => {
                            const cheque =
                                row.original;

                            return (
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            cheque.direction ===
                                            "received"
                                                ? COLORS.green
                                                : COLORS.red,

                                        whiteSpace:
                                            "nowrap",
                                    }}
                                >
                                    {cheque.direction ===
                                    "received"
                                        ? "+"
                                        : "-"}

                                    {formatMoney(
                                        cheque.amount,

                                        cheque.currency
                                    )}
                                </MDTypography>
                            );
                        },
                    },

                    /* =====================================
                       DUE DATE
                    ===================================== */

                    {
                        Header:
                            financeText("DUE DATE"),

                        accessor:
                            "dueDate",

                        Cell: ({
                                   row,
                               }) => {
                            const cheque =
                                row.original;

                            const days =
                                getDaysUntilDue(
                                    cheque.dueDate
                                );

                            const settlementPending =
                                canSettleCheque(
                                    cheque
                                );

                            let color =
                                COLORS.text;

                            let subtitle =
                                "";

                            if (
                                settlementPending &&
                                days !==
                                null
                            ) {
                                if (
                                    days <
                                    0
                                ) {
                                    color =
                                        COLORS.red;

                                    subtitle =
                                        `${Math.abs(
                                            days
                                        )} ${financeText("day overdue")}`;
                                } else if (
                                    days ===
                                    0
                                ) {
                                    color =
                                        COLORS.amber;

                                    subtitle =
                                        financeText("Due today");
                                } else {
                                    subtitle =
                                        `${days} ${financeText("day remaining")}`;

                                    if (
                                        days <=
                                        7
                                    ) {
                                        color =
                                            COLORS.amber;
                                    }
                                }
                            }

                            return (
                                <MDBox>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        display="block"
                                        sx={{
                                            color,
                                        }}
                                    >
                                        {formatDate(
                                            cheque.dueDate
                                        )}
                                    </MDTypography>

                                    {subtitle && (
                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color,
                                            }}
                                        >
                                            {subtitle}
                                        </MDTypography>
                                    )}
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       STATUS
                    ===================================== */

                    {
                        Header:
                            financeText("STATUS"),

                        accessor:
                            "status",

                        Cell: ({
                                   value,
                               }) => {
                            const config =
                                getStatusConfig(
                                    value
                                );

                            return (
                                <Chip
                                    size="small"
                                    label={
                                        financeText(
                                            config.label
                                        )
                                    }
                                    sx={{
                                        color:
                                        config.color,

                                        background:
                                        config.background,

                                        border:
                                            `1px solid ${config.border}`,

                                        fontWeight:
                                            700,
                                    }}
                                />
                            );
                        },
                    },

                    /* =====================================
                       COMPANY CASH
                    ===================================== */

                    {
                        Header:
                            financeText("CASH ACCOUNT"),

                        id:
                            "settlementAccount",

                        Cell: ({
                                   row,
                               }) => {
                            const cheque =
                                row.original;

                            const account =
                                resolveChequeSettlementAccount(
                                    cheque
                                );

                            const settledAt =
                                cheque.settledAt ||
                                cheque.collectedAt;

                            return (
                                <MDBox>
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        display="block"
                                        sx={{
                                            color:
                                                account
                                                    ? COLORS.text
                                                    : COLORS.subtext,
                                        }}
                                    >
                                        {account?.name ||
                                            financeText("Not Settled")}
                                    </MDTypography>

                                    {settledAt && (
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {formatDateTime(
                                                settledAt
                                            )}
                                        </MDTypography>
                                    )}

                                    {cheque.cashTransactionId && (
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.green,

                                                fontWeight:
                                                    700,
                                            }}
                                        >
                                            {financeText("Company Cash posted")}
                                        </MDTypography>
                                    )}
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       ACTIONS
                    ===================================== */

                    {
                        Header:
                            financeText("ACTIONS"),

                        id:
                            "actions",

                        disableSortBy:
                            true,

                        Cell: ({
                                   row,
                               }) => {
                            const cheque =
                                row.original;

                            const settled =
                                isSettledCheque(
                                    cheque
                                );

                            const canSettle =
                                canSettleCheque(
                                    cheque
                                );

                            return (
                                <MDBox
                                    display="flex"
                                    justifyContent="flex-end"
                                    gap={0.55}
                                >
                                    {/* SETTLE */}

                                    <Tooltip
                                        title={
                                            canSettle
                                                ? cheque.direction ===
                                                "received"
                                                    ? financeText("Collect cheque")
                                                    : financeText("Pay cheque")
                                                : cheque.status ===
                                                "endorsed"
                                                    ? financeText("Endorsed cheque cannot be collected to Company Cash")
                                                    : cheque.status ===
                                                    "bounced"
                                                        ? financeText("Bounced cheque cannot be collected")
                                                        : financeText("Cheque cannot be settled in its current status")
                                        }
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    !canSettle
                                                }
                                                onClick={() =>
                                                    openSettle(
                                                        cheque
                                                    )
                                                }
                                                sx={getActionButtonSx(
                                                    "settle"
                                                )}
                                            >
                                                <TaskAltOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    {/* EDIT */}

                                    <Tooltip
                                        title={
                                            settled
                                                ? financeText("Settled cheque cannot be edited")
                                                : financeText("Edit cheque")
                                        }
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    settled
                                                }
                                                onClick={() =>
                                                    openEditCheque(
                                                        cheque
                                                    )
                                                }
                                                sx={getActionButtonSx(
                                                    "edit"
                                                )}
                                            >
                                                <EditOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    {/* DELETE */}

                                    <Tooltip
                                        title={
                                            settled
                                                ? financeText("Settled cheque cannot be deleted")
                                                : financeText("Delete cheque")
                                        }
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    settled
                                                }
                                                onClick={() =>
                                                    openDelete(
                                                        cheque
                                                    )
                                                }
                                                sx={getActionButtonSx(
                                                    "delete"
                                                )}
                                            >
                                                <DeleteOutlineOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </MDBox>
                            );
                        },
                    },
                ],

                rows:
                filteredCheques,
            }),

            [
                filteredCheques,
                resolveChequeSettlementAccount,
                financeLanguage,
            ]
        );

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight:
                        "100vh",

                    background:
                    COLORS.page,

                    mx: {
                        xs:
                            -2,

                        md:
                            -3,
                    },

                    px: {
                        xs:
                            2,

                        md:
                            3,
                    },
                }}
            >
                {/* =================================================
                    HERO
                ================================================= */}

                <Card
                    sx={{
                        borderRadius:
                            "22px",

                        color:
                        COLORS.white,

                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",

                        boxShadow:
                            "0 18px 46px rgba(11,31,58,.22)",

                        overflow:
                            "hidden",

                        position:
                            "relative",

                        mb:
                            3,

                        "&::after": {
                            content:
                                '""',

                            position:
                                "absolute",

                            width:
                                360,

                            height:
                                360,

                            borderRadius:
                                "50%",

                            right:
                                -130,

                            top:
                                -180,

                            background:
                                "rgba(255,255,255,.07)",
                        },

                        "&::before": {
                            content:
                                '""',

                            position:
                                "absolute",

                            width:
                                230,

                            height:
                                230,

                            borderRadius:
                                "50%",

                            right:
                                120,

                            bottom:
                                -170,

                            background:
                                "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox
                        p={{
                            xs:
                                2.5,

                            md:
                                3.5,
                        }}
                        position="relative"
                        zIndex={1}
                    >
                        <Grid
                            container
                            spacing={3}
                            alignItems="center"
                        >
                            <Grid
                                item
                                xs={12}
                                lg={7}
                            >
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            background:
                                                "rgba(255,255,255,.12)",
                                        }}
                                    >
                                        <ReceiptLongOutlined
                                            sx={{
                                                color:
                                                    "#FFFFFF",
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",

                                            letterSpacing:
                                                ".12em",
                                        }}
                                    >
                                        {financeText("CORPORATE TREASURY")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            "#FFFFFF",

                                        lineHeight:
                                            1.12,
                                    }}
                                >
                                    {financeText("Cheques")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color:
                                            "rgba(255,255,255,.72)",

                                        maxWidth:
                                            760,
                                    }}
                                >
                                    {financeText(
                                        "Manage received and issued cheques, maturity dates, portfolio status and Company Cash collections or payments."
                                    )}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    gap={1}
                                    flexWrap="wrap"
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={
                                            <ArrowDownward
                                                sx={{
                                                    color:
                                                        "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${
                                            cheques.filter(
                                                (
                                                    item
                                                ) =>
                                                    item.direction ===
                                                    "received"
                                            ).length
                                        } ${financeText("received")}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <ArrowUpward
                                                sx={{
                                                    color:
                                                        "#FDA29B !important",
                                                }}
                                            />
                                        }
                                        label={`${
                                            cheques.filter(
                                                (
                                                    item
                                                ) =>
                                                    item.direction ===
                                                    "issued"
                                            ).length
                                        } ${financeText("issued")}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <AccountBalanceOutlined
                                                sx={{
                                                    color:
                                                        "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${cashAccounts.length} ${financeText("Company Cash accounts")}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth
                                                sx={{
                                                    color:
                                                        "#B2DDFF !important",
                                                }}
                                            />
                                        }
                                        label={`${financeText("Updated")} ${lastUpdated.toLocaleTimeString(
                                            [],
                                            {
                                                hour:
                                                    "2-digit",

                                                minute:
                                                    "2-digit",
                                            }
                                        )}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                lg={5}
                            >
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background:
                                            "rgba(255,255,255,.09)",

                                        border:
                                            "1px solid rgba(255,255,255,.13)",

                                        backdropFilter:
                                            "blur(8px)",
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",
                                        }}
                                    >
                                        {financeText("RECEIVED CHEQUE PORTFOLIO")}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.3}
                                        sx={{
                                            color:
                                                "#B2DDFF",

                                            fontWeight:
                                                700,
                                        }}
                                    >
                                        {
                                            summaryCurrency
                                        }
                                    </MDTypography>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        mt={0.7}
                                        sx={{
                                            color:
                                                "#FFFFFF",
                                        }}
                                    >
                                        {formatMoney(
                                            summary.receivedPortfolio,

                                            summaryCurrency
                                        )}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.5}
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",
                                        }}
                                    >
                                        {financeText("Issued cheque exposure")}{" "}
                                        <strong>
                                            {formatMoney(
                                                summary.issuedPortfolio,

                                                summaryCurrency
                                            )}
                                        </strong>
                                    </MDTypography>

                                    <MDBox
                                        display="flex"
                                        gap={1}
                                        mt={2}
                                        alignItems="stretch"
                                    >
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {[
                                                { value: "en", label: "EN", flag: "/flags/gb.svg", title: "English" },
                                                { value: "tr", label: "TR", flag: "/flags/tr.svg", title: "Türkçe" },
                                            ].map((language) => (
                                                <MDBox
                                                    key={language.value}
                                                    component="button"
                                                    type="button"
                                                    onClick={() =>
                                                        changeFinanceLanguage({
                                                            target: { value: language.value },
                                                        })
                                                    }
                                                    title={language.title}
                                                    sx={{
                                                        appearance: "none",
                                                        WebkitAppearance: "none",
                                                        border: "none",
                                                        outline: "none",
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: "7px",
                                                        height: 38,
                                                        minWidth: 76,
                                                        px: 1.15,
                                                        borderRadius: "8px",
                                                        background:
                                                            financeLanguage === language.value
                                                                ? "#FFFFFF"
                                                                : "transparent",
                                                        color:
                                                            financeLanguage === language.value
                                                                ? COLORS.navy
                                                                : "#FFFFFF",
                                                        boxShadow:
                                                            financeLanguage === language.value
                                                                ? "0 3px 10px rgba(0,0,0,.18)"
                                                                : "none",
                                                    }}
                                                >
                                                    <MDBox
                                                        component="img"
                                                        src={language.flag}
                                                        alt={language.title}
                                                        sx={{
                                                            width: 24,
                                                            height: 17,
                                                            objectFit: "cover",
                                                            borderRadius: "3px",
                                                            display: "block",
                                                        }}
                                                    />
                                                    <MDTypography
                                                        component="span"
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 800,
                                                            color: "inherit",
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {language.label}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={
                                                openAddCheque
                                            }
                                            sx={{
                                                background:
                                                    "#FFFFFF !important",

                                                color:
                                                    `${COLORS.navy} !important`,

                                                flex:
                                                    1,

                                                boxShadow:
                                                    "none",
                                            }}
                                        >
                                            <AddOutlined
                                                sx={{
                                                    mr:
                                                        0.5,
                                                }}
                                            />

                                            {financeText("Add Cheque")}
                                        </MDButton>

                                        <Tooltip title={financeText("Refresh")}>
                                            <span style={{ display: "inline-flex" }}><IconButton
                                                onClick={
                                                    fetchData
                                                }
                                                disabled={
                                                    loading
                                                }
                                                sx={{
                                                    color:
                                                        "#FFFFFF",

                                                    border:
                                                        "1px solid rgba(255,255,255,.18)",

                                                    borderRadius:
                                                        "10px",
                                                }}
                                            >
                                                {loading ? (
                                                    <CircularProgress
                                                        size={20}
                                                        sx={{
                                                            color:
                                                                "#FFFFFF",
                                                        }}
                                                    />
                                                ) : (
                                                    <RefreshOutlined />
                                                )}
                                            </IconButton></span>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {/* =================================================
                    SUMMARY CURRENCY
                ================================================= */}

                <MDBox
                    display="flex"
                    justifyContent={{
                        xs:
                            "stretch",

                        sm:
                            "flex-end",
                    }}
                    mb={1.5}
                >
                    <TextField
                        select
                        size="small"
                        label={financeText("Summary Currency")}
                        value={
                            summaryCurrency
                        }
                        onChange={(
                            event
                        ) =>
                            setSummaryCurrency(
                                event.target.value
                            )
                        }
                        sx={{
                            width: {
                                xs:
                                    "100%",

                                sm:
                                    210,
                            },

                            "& .MuiOutlinedInput-root":
                                {
                                    borderRadius:
                                        "11px",

                                    background:
                                        "#FFFFFF",
                                },
                        }}
                    >
                        {CURRENCIES.map(
                            (
                                item
                            ) => (
                                <MenuItem
                                    key={
                                        item
                                    }
                                    value={
                                        item
                                    }
                                >
                                    {item}
                                </MenuItem>
                            )
                        )}
                    </TextField>
                </MDBox>

                {/* =================================================
                    METRICS
                ================================================= */}

                <Grid
                    container
                    spacing={2.5}
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Received Cheque Portfolio")}
                            value={formatMoney(
                                summary.receivedPortfolio,

                                summaryCurrency
                            )}
                            subtitle={`${summary.receivedCount} ${financeText("received")} ${summaryCurrency} ${financeText("cheques")}`}
                            icon={
                                <ArrowDownward />
                            }
                            tone="green"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Issued Cheque Exposure")}
                            value={formatMoney(
                                summary.issuedPortfolio,

                                summaryCurrency
                            )}
                            subtitle={`${summary.issuedCount} ${financeText("issued")} ${summaryCurrency} ${financeText("cheques")}`}
                            icon={
                                <ArrowUpward />
                            }
                            tone="red"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Due Within 7 Days")}
                            value={
                                summary.dueSoon
                            }
                            subtitle={financeText("Upcoming cheque maturities")}
                            icon={
                                <EventOutlined />
                            }
                            tone="amber"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Overdue")}
                            value={
                                summary.overdue
                            }
                            subtitle={financeText("Unsettled overdue cheques")}
                            icon={
                                <WarningAmberRounded />
                            }
                            tone={
                                summary.overdue >
                                0
                                    ? "red"
                                    : "navy"
                            }
                        />
                    </Grid>
                </Grid>

                {/* =================================================
                    TABLE CARD
                ================================================= */}

                <Card
                    sx={{
                        ...cardSx,

                        mt:
                            2.5,
                    }}
                >
                    <MDBox
                        p={3}
                    >
                        {/* HEADER */}

                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems={{
                                xs:
                                    "flex-start",

                                md:
                                    "center",
                            }}
                            flexDirection={{
                                xs:
                                    "column",

                                md:
                                    "row",
                            }}
                            gap={1.5}
                            mb={2.5}
                        >
                            <MDBox>
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {financeText("Cheque Portfolio")}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {financeText(
                                        "Review received and issued cheques, due dates, portfolio movements and Company Cash settlements."
                                    )}
                                </MDTypography>
                            </MDBox>

                            <MDBox
                                display="flex"
                                gap={1}
                            >
                                <MDButton
                                    variant="contained"
                                    onClick={
                                        openAddCheque
                                    }
                                    sx={{
                                        background:
                                            `${COLORS.blue} !important`,

                                        color:
                                            "#FFFFFF !important",

                                        borderRadius:
                                            "10px",

                                        boxShadow:
                                            "none",
                                    }}
                                >
                                    <AddOutlined
                                        sx={{
                                            mr:
                                                0.5,
                                        }}
                                    />

                                    {financeText("Add Cheque")}
                                </MDButton>

                                <MDButton
                                    variant="outlined"
                                    color="dark"
                                    onClick={
                                        fetchData
                                    }
                                    disabled={
                                        loading
                                    }
                                >
                                    <RefreshOutlined
                                        sx={{
                                            mr:
                                                0.5,
                                        }}
                                    />

                                    {financeText("Refresh")}
                                </MDButton>
                            </MDBox>
                        </MDBox>

                        {/* FILTERS */}

                        <Grid
                            container
                            spacing={2}
                        >
                            <Grid
                                item
                                xs={12}
                                lg={5}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={financeText("Search")}
                                    placeholder={financeText("Cheque, bank, branch, drawer, beneficiary...")}
                                    value={
                                        search
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setSearch(
                                            event.target.value
                                        )
                                    }
                                    InputProps={{
                                        startAdornment:
                                            (
                                                <InputAdornment position="start">
                                                    <SearchOutlined fontSize="small" />
                                                </InputAdornment>
                                            ),
                                    }}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={4}
                                lg={2.5}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Direction")}
                                    value={
                                        directionFilter
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setDirectionFilter(
                                            event.target.value
                                        )
                                    }
                                >
                                    <MenuItem value="all">
                                        {financeText("All Directions")}
                                    </MenuItem>

                                    <MenuItem value="received">
                                        {financeText("Received")}
                                    </MenuItem>

                                    <MenuItem value="issued">
                                        {financeText("Issued")}
                                    </MenuItem>
                                </TextField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={4}
                                lg={2.5}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Status")}
                                    value={
                                        statusFilter
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setStatusFilter(
                                            event.target.value
                                        )
                                    }
                                >
                                    <MenuItem value="all">
                                        {financeText("All Statuses")}
                                    </MenuItem>

                                    {STATUSES.map(
                                        (
                                            item
                                        ) => (
                                            <MenuItem
                                                key={
                                                    item.value
                                                }
                                                value={
                                                    item.value
                                                }
                                            >
                                                {
                                                    financeText(item.label)
                                                }
                                            </MenuItem>
                                        )
                                    )}
                                </TextField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={4}
                                lg={2}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Currency")}
                                    value={
                                        currencyFilter
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setCurrencyFilter(
                                            event.target.value
                                        )
                                    }
                                >
                                    <MenuItem value="all">
                                        {financeText("All Currencies")}
                                    </MenuItem>

                                    {CURRENCIES.map(
                                        (
                                            item
                                        ) => (
                                            <MenuItem
                                                key={
                                                    item
                                                }
                                                value={
                                                    item
                                                }
                                            >
                                                {
                                                    item
                                                }
                                            </MenuItem>
                                        )
                                    )}
                                </TextField>
                            </Grid>
                        </Grid>

                        {/* INFO */}

                        <MDBox
                            mt={2}
                            p={1.5}
                            borderRadius="8px"
                            display="flex"
                            justifyContent="space-between"
                            alignItems={{
                                xs:
                                    "flex-start",

                                md:
                                    "center",
                            }}
                            flexDirection={{
                                xs:
                                    "column",

                                md:
                                    "row",
                            }}
                            gap={1}
                            sx={{
                                background:
                                    "#F9FAFB",

                                border:
                                    `1px solid ${COLORS.border}`,
                            }}
                        >
                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText("Showing")}{" "}
                                <strong
                                    style={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {
                                        filteredCheques.length
                                    }
                                </strong>{" "}
                                {financeText("of")}{" "}
                                <strong
                                    style={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {
                                        cheques.length
                                    }
                                </strong>{" "}
                                {financeText("cheques")}
                            </MDTypography>

                            <MDBox
                                display="flex"
                                gap={0.7}
                                flexWrap="wrap"
                            >
                                <Chip
                                    size="small"
                                    label={`${financeText("Portfolio")} ${
                                        cheques.filter(
                                            (
                                                item
                                            ) =>
                                                item.status ===
                                                "portfolio"
                                        ).length
                                    }`}
                                    sx={{
                                        color:
                                        COLORS.amber,

                                        background:
                                        COLORS.amberLight,

                                        fontWeight:
                                            700,
                                    }}
                                />

                                <Chip
                                    size="small"
                                    label={`${financeText("Collected")} ${
                                        cheques.filter(
                                            (
                                                item
                                            ) =>
                                                item.status ===
                                                "collected"
                                        ).length
                                    }`}
                                    sx={{
                                        color:
                                        COLORS.green,

                                        background:
                                        COLORS.greenLight,

                                        fontWeight:
                                            700,
                                    }}
                                />

                                <Chip
                                    size="small"
                                    label={`${financeText("Paid")} ${
                                        cheques.filter(
                                            (
                                                item
                                            ) =>
                                                item.status ===
                                                "paid"
                                        ).length
                                    }`}
                                    sx={{
                                        color:
                                        COLORS.green,

                                        background:
                                        COLORS.greenLight,

                                        fontWeight:
                                            700,
                                    }}
                                />
                            </MDBox>
                        </MDBox>

                        {/* TABLE */}

                        <MDBox
                            mt={2.5}
                            sx={{
                                "& .MuiTableContainer-root":
                                    {
                                        border:
                                            `1px solid ${COLORS.border}`,

                                        borderRadius:
                                            "10px",

                                        overflowX:
                                            "auto",

                                        boxShadow:
                                            "0 5px 18px rgba(16,24,40,.04)",
                                    },

                                "& table":
                                    {
                                        minWidth:
                                            1550,
                                    },

                                "& table thead":
                                    {
                                        background:
                                            "#F8FAFC",
                                    },

                                "& table thead th":
                                    {
                                        color:
                                            "#475467 !important",

                                        fontSize:
                                            "10px !important",

                                        fontWeight:
                                            "800 !important",

                                        letterSpacing:
                                            ".08em",

                                        paddingTop:
                                            "15px !important",

                                        paddingBottom:
                                            "15px !important",
                                    },

                                "& table tbody td":
                                    {
                                        paddingTop:
                                            "13px !important",

                                        paddingBottom:
                                            "13px !important",

                                        verticalAlign:
                                            "middle",
                                    },

                                "& table tbody tr:hover":
                                    {
                                        background:
                                            "#F5F8FF !important",

                                        boxShadow:
                                            "inset 3px 0 0 #175CD3",
                                    },
                            }}
                        >
                            {loading ? (
                                <MDBox
                                    py={8}
                                    display="flex"
                                    justifyContent="center"
                                >
                                    <CircularProgress />
                                </MDBox>
                            ) : (
                                <DataTable
                                    table={
                                        tableData
                                    }
                                    canSearch={
                                        false
                                    }
                                    entriesPerPage={{
                                        defaultValue:
                                            10,

                                        entries: [
                                            5,
                                            10,
                                            25,
                                            50,
                                            100,
                                        ],
                                    }}
                                    showTotalEntries
                                    isSorted
                                    noEndBorder
                                />
                            )}
                        </MDBox>
                    </MDBox>
                </Card>
            </MDBox>

            {/* =====================================================
                ADD / EDIT CHEQUE
            ===================================================== */}

            <Dialog
                open={
                    chequeModal
                }
                onClose={
                    closeChequeModal
                }
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogTitle>
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <MDBox
                            width={44}
                            height={44}
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                color:
                                    editingCheque
                                        ? COLORS.purple
                                        : COLORS.blue,

                                background:
                                    editingCheque
                                        ? COLORS.purpleLight
                                        : COLORS.blueLight,
                            }}
                        >
                            {editingCheque ? (
                                <EditOutlined />
                            ) : (
                                <ReceiptLongOutlined />
                            )}
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    color:
                                    COLORS.text,
                                }}
                            >
                                {financeText(
                                    editingCheque
                                        ? "Edit Cheque"
                                        : "Add Cheque"
                                )}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText("Register received or issued cheque information.")}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent>
                    <Grid
                        container
                        spacing={2}
                        mt={0}
                    >
                        {/* DIRECTION */}

                        <Grid
                            item
                            xs={12}
                            sm={4}
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Direction")}
                                value={
                                    chequeForm.direction
                                }
                                disabled={
                                    Boolean(
                                        editingCheque
                                    )
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleDirectionChange(
                                        event.target.value
                                    )
                                }
                                helperText={
                                    editingCheque
                                        ? financeText("Direction cannot be changed after creation.")
                                        : ""
                                }
                            >
                                {DIRECTIONS.map(
                                    (
                                        item
                                    ) => (
                                        <MenuItem
                                            key={
                                                item.value
                                            }
                                            value={
                                                item.value
                                            }
                                        >
                                            {
                                                financeText(item.label)
                                            }
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        </Grid>

                        {/* CHEQUE NUMBER */}

                        <Grid
                            item
                            xs={12}
                            sm={4}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Cheque No")}
                                value={
                                    chequeForm.chequeNo
                                }
                                onChange={(
                                    event
                                ) =>
                                    setChequeForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            chequeNo:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* CURRENCY */}

                        <Grid
                            item
                            xs={12}
                            sm={4}
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Currency")}
                                value={
                                    chequeForm.currency
                                }
                                onChange={(
                                    event
                                ) =>
                                    setChequeForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            currency:
                                            event.target.value,
                                        })
                                    )
                                }
                            >
                                {CURRENCIES.map(
                                    (
                                        item
                                    ) => (
                                        <MenuItem
                                            key={
                                                item
                                            }
                                            value={
                                                item
                                            }
                                        >
                                            {
                                                item
                                            }
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        </Grid>

                        {/* AMOUNT */}

                        <Grid
                            item
                            xs={12}
                            sm={4}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("Amount")}
                                value={
                                    chequeForm.amount
                                }
                                inputProps={{
                                    min:
                                        0,

                                    step:
                                        "0.01",
                                }}
                                onChange={(
                                    event
                                ) =>
                                    setChequeForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            amount:
                                            event.target.value,
                                        })
                                    )
                                }
                                InputProps={{
                                    startAdornment:
                                        (
                                            <InputAdornment position="start">
                                                {
                                                    chequeForm.currency
                                                }
                                            </InputAdornment>
                                        ),
                                }}
                            />
                        </Grid>

                        {/* ISSUE DATE */}

                        <Grid
                            item
                            xs={12}
                            sm={4}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label={financeText("Issue Date")}
                                value={
                                    chequeForm.issueDate
                                }
                                onChange={(
                                    event
                                ) =>
                                    setChequeForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            issueDate:
                                            event.target.value,
                                        })
                                    )
                                }
                                InputLabelProps={{
                                    shrink:
                                        true,
                                }}
                            />
                        </Grid>

                        {/* DUE DATE */}

                        <Grid
                            item
                            xs={12}
                            sm={4}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label={financeText("Due Date")}
                                value={
                                    chequeForm.dueDate
                                }
                                onChange={(
                                    event
                                ) =>
                                    setChequeForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            dueDate:
                                            event.target.value,
                                        })
                                    )
                                }
                                InputLabelProps={{
                                    shrink:
                                        true,
                                }}
                            />
                        </Grid>

                        {/* BANK */}

                        <Grid
                            item
                            xs={12}
                            sm={4}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Bank Name")}
                                value={
                                    chequeForm.bankName
                                }
                                onChange={(
                                    event
                                ) =>
                                    setChequeForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            bankName:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* BRANCH */}

                        <Grid
                            item
                            xs={12}
                            sm={4}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Branch")}
                                value={
                                    chequeForm.branch
                                }
                                onChange={(
                                    event
                                ) =>
                                    setChequeForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            branch:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* ACCOUNT NO */}

                        <Grid
                            item
                            xs={12}
                            sm={4}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Account No")}
                                value={
                                    chequeForm.accountNo
                                }
                                onChange={(
                                    event
                                ) =>
                                    setChequeForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            accountNo:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* DRAWER */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Drawer Name")}
                                value={
                                    chequeForm.drawerName
                                }
                                onChange={(
                                    event
                                ) =>
                                    setChequeForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            drawerName:
                                            event.target.value,
                                        })
                                    )
                                }
                                helperText={financeText("Person/company issuing the cheque.")}
                            />
                        </Grid>

                        {/* BENEFICIARY */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Beneficiary Name")}
                                value={
                                    chequeForm.beneficiaryName
                                }
                                onChange={(
                                    event
                                ) =>
                                    setChequeForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            beneficiaryName:
                                            event.target.value,
                                        })
                                    )
                                }
                                helperText={financeText("Person/company receiving the cheque.")}
                            />
                        </Grid>

                        {/* STATUS */}

                        {editingCheque && (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Status")}
                                    value={
                                        chequeForm.status
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setChequeForm(
                                            (
                                                previous
                                            ) => ({
                                                ...previous,

                                                status:
                                                event.target.value,
                                            })
                                        )
                                    }
                                    helperText={financeText("Collected/Paid status can only be created through settlement.")}
                                >
                                    {getEditableStatusesForDirection(
                                        chequeForm.direction
                                    ).map(
                                        (
                                            item
                                        ) => (
                                            <MenuItem
                                                key={
                                                    item.value
                                                }
                                                value={
                                                    item.value
                                                }
                                            >
                                                {
                                                    financeText(item.label)
                                                }
                                            </MenuItem>
                                        )
                                    )}
                                </TextField>
                            </Grid>
                        )}

                        {/* STATUS INFO */}

                        {editingCheque &&
                            chequeForm.status ===
                            "endorsed" && (
                                <Grid
                                    item
                                    xs={12}
                                >
                                    <Alert
                                        severity="info"
                                        sx={{
                                            borderRadius:
                                                "8px",
                                        }}
                                    >
                                        {financeText(
                                            "An endorsed cheque has been transferred to another party and cannot be collected directly into Company Cash."
                                        )}
                                    </Alert>
                                </Grid>
                            )}

                        {editingCheque &&
                            chequeForm.status ===
                            "bounced" && (
                                <Grid
                                    item
                                    xs={12}
                                >
                                    <Alert
                                        severity="warning"
                                        sx={{
                                            borderRadius:
                                                "8px",
                                        }}
                                    >
                                        {financeText(
                                            "A bounced cheque cannot be settled into Company Cash until its business status is resolved."
                                        )}
                                    </Alert>
                                </Grid>
                            )}

                        {/* DESCRIPTION */}

                        <Grid
                            item
                            xs={12}
                        >
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label={financeText("Description")}
                                value={
                                    chequeForm.description
                                }
                                onChange={(
                                    event
                                ) =>
                                    setChequeForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            description:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* ACCOUNTING INFO */}

                        <Grid
                            item
                            xs={12}
                        >
                            <Alert
                                severity="info"
                                sx={{
                                    borderRadius:
                                        "8px",
                                }}
                            >
                                {financeText(
                                    chequeForm.direction ===
                                    "received"
                                        ? "A received cheque does not increase Company Cash when it is registered. Company Cash increases only after the cheque is collected."
                                        : "An issued cheque does not reduce Company Cash when it is registered. Company Cash decreases only when the cheque is paid."
                                )}
                            </Alert>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        px:
                            3,

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            saving
                        }
                        onClick={
                            closeChequeModal
                        }
                    >
                        {financeText("Close")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            saving
                        }
                        onClick={
                            saveCheque
                        }
                        sx={{
                            background:
                                `${COLORS.blue} !important`,

                            color:
                                "#FFFFFF !important",

                            borderRadius:
                                "10px",

                            boxShadow:
                                "none",
                        }}
                    >
                        {saving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText(
                            editingCheque
                                ? "Update Cheque"
                                : "Add Cheque"
                        )}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                SETTLE
            ===================================================== */}

            <Dialog
                open={Boolean(
                    settleTarget
                )}
                onClose={
                    closeSettle
                }
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogTitle>
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <MDBox
                            width={44}
                            height={44}
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                color:
                                    settleTarget?.direction ===
                                    "received"
                                        ? COLORS.green
                                        : COLORS.red,

                                background:
                                    settleTarget?.direction ===
                                    "received"
                                        ? COLORS.greenLight
                                        : COLORS.redLight,

                                border:
                                    settleTarget?.direction ===
                                    "received"
                                        ? "1px solid #ABEFC6"
                                        : "1px solid #FECDCA",
                            }}
                        >
                            <CheckCircleOutline />
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    color:
                                    COLORS.text,
                                }}
                            >
                                {financeText(
                                    settleTarget?.direction ===
                                    "received"
                                        ? "Collect Cheque"
                                        : "Pay Cheque"
                                )}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText("This operation will update Company Cash automatically.")}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent>
                    {settleTarget && (
                        <Stack
                            spacing={2}
                            mt={1}
                        >
                            {/* CHEQUE */}

                            <MDBox
                                p={2}
                                borderRadius="8px"
                                sx={{
                                    background:
                                        "#F9FAFB",

                                    border:
                                        `1px solid ${COLORS.border}`,
                                }}
                            >
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    display="block"
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {financeText("Cheque")} #
                                    {
                                        settleTarget.chequeNo
                                    }
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {settleTarget.bankName ||
                                        "-"}

                                    {" · "}

                                    {financeText("Due")}{" "}

                                    {formatDate(
                                        settleTarget.dueDate
                                    )}
                                </MDTypography>

                                <MDTypography
                                    variant="h4"
                                    fontWeight="bold"
                                    mt={1.5}
                                    sx={{
                                        color:
                                            settleTarget.direction ===
                                            "received"
                                                ? COLORS.green
                                                : COLORS.red,
                                    }}
                                >
                                    {formatMoney(
                                        settleTarget.amount,

                                        settleTarget.currency
                                    )}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {financeText(
                                        settleTarget.direction ===
                                        "received"
                                            ? "Amount to be added to Company Cash"
                                            : "Amount to be deducted from Company Cash"
                                    )}
                                </MDTypography>
                            </MDBox>

                            {/* ACCOUNT */}

                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText(
                                    settleTarget.direction ===
                                    "received"
                                        ? "Collection Account"
                                        : "Payment Account"
                                )}
                                value={
                                    settleForm.cashAccountId
                                }
                                onChange={(
                                    event
                                ) =>
                                    setSettleForm({
                                        cashAccountId:
                                        event.target.value,
                                    })
                                }
                                helperText={`Only active ${settleTarget.currency} Company Cash accounts are shown.`}
                            >
                                {settlementAccounts.length ===
                                    0 && (
                                        <MenuItem
                                            value=""
                                            disabled
                                        >
                                            {financeText("No suitable Company Cash account")}
                                        </MenuItem>
                                    )}

                                {settlementAccounts.map(
                                    (
                                        account
                                    ) => (
                                        <MenuItem
                                            key={
                                                getId(
                                                    account
                                                )
                                            }
                                            value={
                                                getId(
                                                    account
                                                )
                                            }
                                        >
                                            {
                                                account.name
                                            }

                                            {" · "}

                                            {account.type ===
                                            "cash"
                                                ? financeText("Cash")
                                                : financeText("Bank")}

                                            {" · "}

                                            {formatMoney(
                                                account.currentBalance,

                                                account.currency
                                            )}
                                        </MenuItem>
                                    )
                                )}
                            </TextField>

                            {/* PREVIEW */}

                            {selectedSettlementAccount &&
                                settlementPreview && (
                                    <MDBox
                                        p={2}
                                        borderRadius="8px"
                                        sx={{
                                            background:
                                                settlementPreview.insufficientBalance
                                                    ? COLORS.redLight
                                                    : settleTarget.direction ===
                                                    "received"
                                                        ? COLORS.greenLight
                                                        : COLORS.blueLight,

                                            border:
                                                settlementPreview.insufficientBalance
                                                    ? "1px solid #FECDCA"
                                                    : settleTarget.direction ===
                                                    "received"
                                                        ? "1px solid #ABEFC6"
                                                        : "1px solid #B2DDFF",
                                        }}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                    settlementPreview.insufficientBalance
                                                        ? COLORS.red
                                                        : settleTarget.direction ===
                                                        "received"
                                                            ? COLORS.green
                                                            : COLORS.blue,
                                            }}
                                        >
                                            {financeText("COMPANY CASH PREVIEW")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            mt={0.5}
                                            sx={{
                                                color:
                                                COLORS.text,
                                            }}
                                        >
                                            {
                                                selectedSettlementAccount.name
                                            }
                                        </MDTypography>

                                        <Grid
                                            container
                                            spacing={1.5}
                                            mt={0.3}
                                        >
                                            <Grid
                                                item
                                                xs={6}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{
                                                        color:
                                                        COLORS.subtext,
                                                    }}
                                                >
                                                    {financeText("Current Balance")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                >
                                                    {formatMoney(
                                                        settlementPreview.currentBalance,

                                                        selectedSettlementAccount.currency
                                                    )}
                                                </MDTypography>
                                            </Grid>

                                            <Grid
                                                item
                                                xs={6}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{
                                                        color:
                                                        COLORS.subtext,
                                                    }}
                                                >
                                                    {financeText("Balance After Settlement")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    sx={{
                                                        color:
                                                            settlementPreview.balanceAfter >=
                                                            0
                                                                ? COLORS.green
                                                                : COLORS.red,
                                                    }}
                                                >
                                                    {formatMoney(
                                                        settlementPreview.balanceAfter,

                                                        selectedSettlementAccount.currency
                                                    )}
                                                </MDTypography>
                                            </Grid>
                                        </Grid>
                                    </MDBox>
                                )}

                            {/* ERROR */}

                            {settlementPreview?.insufficientBalance && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        borderRadius:
                                            "8px",
                                    }}
                                >
                                    {financeText(
                                        "Selected Company Cash account has insufficient balance to pay this issued cheque."
                                    )}
                                </Alert>
                            )}

                            {/* ACCOUNTING */}

                            <Alert
                                severity="warning"
                                sx={{
                                    borderRadius:
                                        "8px",
                                }}
                            >
                                {financeText(
                                    settleTarget.direction ===
                                    "received"
                                        ? "After confirmation, status becomes Collected and a Company Cash Cash In transaction with payment method Cheque is created."
                                        : "After confirmation, status becomes Paid and a Company Cash Cash Out transaction with payment method Cheque is created."
                                )}
                            </Alert>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        px:
                            3,

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            saving
                        }
                        onClick={
                            closeSettle
                        }
                    >
                        Cancel
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            saving ||
                            !settleForm.cashAccountId ||
                            !selectedSettlementAccount ||
                            settlementPreview?.insufficientBalance
                        }
                        onClick={
                            settleCheque
                        }
                        sx={{
                            background:
                                settleTarget?.direction ===
                                "received"
                                    ? `${COLORS.green} !important`
                                    : `${COLORS.red} !important`,

                            color:
                                "#FFFFFF !important",

                            borderRadius:
                                "10px",

                            boxShadow:
                                "none",
                        }}
                    >
                        {saving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText(
                            settleTarget?.direction ===
                            "received"
                                ? "Collect Cheque"
                                : "Pay Cheque"
                        )}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                DELETE
            ===================================================== */}

            <Dialog
                open={Boolean(
                    deleteTarget
                )}
                onClose={() => {
                    if (
                        !saving
                    ) {
                        setDeleteTarget(
                            null
                        );
                    }
                }}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        textAlign:
                            "center",

                        pt:
                            4,
                    }}
                >
                    <MDBox
                        width={58}
                        height={58}
                        borderRadius="50%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mx="auto"
                        mb={2}
                        sx={{
                            background:
                            COLORS.redLight,
                        }}
                    >
                        <WarningAmberRounded
                            sx={{
                                color:
                                COLORS.red,

                                fontSize:
                                    30,
                            }}
                        />
                    </MDBox>

                    <MDTypography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                            color:
                            COLORS.text,
                        }}
                    >
                        {financeText("Delete Cheque?")}
                    </MDTypography>

                    <MDTypography
                        variant="body2"
                        mt={1}
                        sx={{
                            color:
                            COLORS.subtext,
                        }}
                    >
                        {financeText("Cheque")}{" "}
                        <strong>
                            #
                            {
                                deleteTarget?.chequeNo
                            }
                        </strong>{" "}
                        {financeText("will be permanently deleted.")}
                    </MDTypography>

                    {deleteTarget && (
                        <Chip
                            sx={{
                                mt:
                                    2,

                                color:
                                    deleteTarget.direction ===
                                    "received"
                                        ? COLORS.green
                                        : COLORS.red,

                                background:
                                    deleteTarget.direction ===
                                    "received"
                                        ? COLORS.greenLight
                                        : COLORS.redLight,

                                fontWeight:
                                    700,
                            }}
                            label={formatMoney(
                                deleteTarget.amount,

                                deleteTarget.currency
                            )}
                        />
                    )}

                    <Alert
                        severity="warning"
                        sx={{
                            mt:
                                2,

                            textAlign:
                                "left",

                            borderRadius:
                                "8px",
                        }}
                    >
                        {financeText(
                            financeText("Collected or paid cheques cannot be deleted because they already affected Company Cash.")
                        )}
                    </Alert>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent:
                            "center",

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            saving
                        }
                        onClick={() =>
                            setDeleteTarget(
                                null
                            )
                        }
                    >
                        Cancel
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            saving
                        }
                        onClick={
                            confirmDelete
                        }
                        sx={{
                            background:
                                `${COLORS.red} !important`,

                            color:
                                "#FFFFFF !important",

                            borderRadius:
                                "10px",

                            boxShadow:
                                "none",
                        }}
                    >
                        {saving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText("Delete Cheque")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                SNACKBAR
            ===================================================== */}

            <Snackbar
                open={
                    notification.open
                }
                autoHideDuration={
                    4500
                }
                onClose={
                    closeNotification
                }
                anchorOrigin={{
                    vertical:
                        "bottom",

                    horizontal:
                        "right",
                }}
            >
                <Alert
                    severity={
                        notification.severity
                    }
                    variant="filled"
                    onClose={
                        closeNotification
                    }
                    sx={{
                        width:
                            "100%",

                        borderRadius:
                            "8px",
                    }}
                >
                    {
                        notification.message
                    }
                </Alert>
            </Snackbar>

            <Footer />
        </DashboardLayout>
    );
};

export default Cheques;